import {
  DownloadOutlined,
  PlusOutlined,
  ReloadOutlined
} from "@ant-design/icons"
import {
  App,
  Button,
  DatePicker,
  Empty,
  Flex,
  Input,
  Select,
  Spin,
  Typography
} from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EntityTable } from "../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../components/EntityTable/surfaces.ts"
import { FilePreviewModal } from "../../components/FilePreviewModal/FilePreviewModal.tsx"
import { TableResultCount } from "../../components/Table/TableResultCount.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../hooks/use-date-format.ts"
import { useScrolledTable } from "../../hooks/use-scrolled-table.ts"
import { nextPageUrl } from "../../redux/shared/paged-list.ts"
import { downloadBlob } from "../../utils/download-file.ts"
import { extractParamsFromNextUrl } from "../../utils/form/extract-params-from-next-url.ts"
import { showNotification } from "../../utils/notification/show-notification.ts"
import { getCustomers } from "../CustomersPage/api/services.ts"
import { IssueInvoiceDrawer } from "../Project/ProjectViewPage/components/sections/ProjectViewFinance/ProjectViewFinance/IssueInvoiceDrawer.tsx"
import { INVOICE_STATUSES_MAP } from "../Project/ProjectViewPage/constants/invoice-statuses.ts"
import type { InvoicesListParams } from "./api/invoices.services.ts"
import { ChangeInvoiceStatusModal } from "./components/ChangeInvoiceStatusModal.tsx"
import { InvoiceDeliveryHistoryModal } from "./components/InvoiceDeliveryHistoryModal.tsx"
import { SendInvoiceConfirmModal } from "./components/SendInvoiceConfirmModal.tsx"
import { type InvoiceRowAction, getColumns } from "./components/columns.tsx"
import { handleInvoiceApiError } from "./helpers/handle-invoice-api-error.ts"
import { useInvoiceDocumentPreview } from "./hooks/use-invoice-document-preview.ts"
import {
  invoicesResetFilters,
  invoicesSetFilters,
  invoicesSetOrdering
} from "./redux/invoices.slice.ts"
import {
  deleteInvoice,
  downloadInvoicePdf,
  exportInvoicesList,
  fetchInvoicesList,
  issueInvoice,
  patchGeneratedInvoice,
  sendInvoice,
  voidInvoice
} from "./redux/invoices.thunks.ts"
import {
  INVOICE_ORIGINS,
  INVOICE_STATUSES,
  type InvoiceListItem
} from "./types/invoices.types.ts"

const { Title } = Typography
const { RangePicker } = DatePicker

const InvoicesPage = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { modal, notification } = App.useApp()
  const { normalizeDate } = useDateFormat()
  const [fetchFailed, setFetchFailed] = useState(false)
  const [sendTarget, setSendTarget] = useState<InvoiceListItem | null>(null)
  const [sending, setSending] = useState(false)
  const [statusTarget, setStatusTarget] = useState<InvoiceListItem | null>(null)
  const [changingStatus, setChangingStatus] = useState(false)
  const [deliveryTarget, setDeliveryTarget] = useState<InvoiceListItem | null>(
    null
  )

  const items = useAppSelector((state) => state.invoices.items)
  const count = useAppSelector((state) => state.invoices.count)
  const next = useAppSelector((state) => state.invoices.next)
  const retryNext = useAppSelector((state) => state.invoices.retryNext)
  const fetching = useAppSelector((state) => state.invoices.fetching)
  const exporting = useAppSelector((state) => state.invoices.exporting)
  const filters = useAppSelector((state) => state.invoices.filters)
  const ordering = useAppSelector((state) => state.invoices.ordering)
  const permissions = useAppSelector((state) => state.auth.permissions)
  const userLabels = useAppSelector((state) => state.auth.permissions?.labels)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState<InvoiceListItem | null>(
    null
  )
  const [customerOptions, setCustomerOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [customerSearching, setCustomerSearching] = useState(false)
  const [searchTerm, setSearchTerm] = useState(filters.q)
  const customerSearchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  const searchCustomers = useCallback((term: string) => {
    clearTimeout(customerSearchTimer.current)
    if (term.length < 3) return
    customerSearchTimer.current = setTimeout(async () => {
      setCustomerSearching(true)
      const response = await getCustomers({ q: term })
      setCustomerSearching(false)
      if (response.ok && response.data) {
        setCustomerOptions(
          response.data.results.map((customer) => ({
            value: customer.id,
            label:
              [customer.first_name, customer.last_name]
                .filter(Boolean)
                .join(" ") || customer.email
          }))
        )
      }
    }, 300)
  }, [])

  const editAllowed = Boolean(
    permissions?.codes?.projects?.invoices?.project_invoice_edit
  )
  // Void requires the GCC owner or admin label (enforced server-side too).
  const voidAllowed = Boolean(
    userLabels?.some(
      (label) => label.code === "gcc" || label.code === "gcc_admin"
    )
  )

  const handleLoadMore = useCallback(() => {
    const url = nextPageUrl({ next, retryNext })
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    dispatch(
      fetchInvoicesList({
        ...extractParamsFromNextUrl<InvoicesListParams>(url),
        isMore: true
      })
    )
  }, [next, retryNext, dispatch])

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: fetching,
    next: next,
    loadMore: handleLoadMore,
    fullPage: true
  })

  const buildParams = useCallback(() => {
    return {
      page: filters.page,
      page_size: filters.page_size,
      ...(filters.q ? { q: filters.q } : {}),
      ...(filters.customer ? { customer: filters.customer } : {}),
      ...(filters.origin ? { origin: filters.origin } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.date_issued_from
        ? { date_issued_from: filters.date_issued_from }
        : {}),
      ...(filters.date_issued_to
        ? { date_issued_to: filters.date_issued_to }
        : {}),
      ...(filters.due_date_from
        ? { due_date_from: filters.due_date_from }
        : {}),
      ...(filters.due_date_to ? { due_date_to: filters.due_date_to } : {}),
      ...(ordering ? { ordering } : {})
    }
  }, [filters, ordering])

  const updateData = useCallback(() => {
    dispatch(fetchInvoicesList(buildParams()))
      .unwrap()
      .then(() => setFetchFailed(false))
      .catch(() => setFetchFailed(true))
  }, [dispatch, buildParams])

  useEffect(() => {
    generateDocumentTitle(t("invoices.title"))
  }, [t])

  useEffect(() => {
    setSearchTerm(filters.q)
  }, [filters.q])

  useEffect(() => {
    updateData()
  }, [updateData])

  const handleExport = async () => {
    const blob = await dispatch(exportInvoicesList(buildParams())).unwrap()
    downloadBlob(blob, "invoices.xlsx")
  }

  const translate = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      t(key, options) as string,
    [t]
  )

  const openNewInvoice = () => {
    setEditingInvoice(null)
    setDrawerOpen(true)
  }

  const reportError = useCallback(
    (payload: unknown) =>
      handleInvoiceApiError({ payload, modal, t: translate, navigate }),
    [modal, translate, navigate]
  )

  // A blob response carries no readable status, so one generic message has to cover every failure.
  const reportPreviewError = useCallback(
    () => showNotification.error({ message: t("invoices.previewFailed") }),
    [t]
  )

  const preview = useInvoiceDocumentPreview(reportPreviewError)

  const handleSelectRow = async (
    record: InvoiceListItem,
    action: InvoiceRowAction
  ) => {
    switch (action) {
      case "edit":
        setEditingInvoice(record)
        setDrawerOpen(true)
        return
      case "issue":
        try {
          await dispatch(issueInvoice(record.id)).unwrap()
          updateData()
        } catch (payload) {
          reportError(payload)
        }
        return
      case "delete":
        modal.confirm({
          title: t("invoices.deleteConfirm.title"),
          content: t("invoices.deleteConfirm.body"),
          okText: t("invoices.deleteConfirm.ok"),
          okButtonProps: { danger: true },
          cancelText: t("invoices.deleteConfirm.cancel"),
          onOk: async () => {
            try {
              await dispatch(deleteInvoice(record.id)).unwrap()
              updateData()
            } catch (payload) {
              reportError(payload)
            }
          }
        })
        return
      case "void":
        modal.confirm({
          title: t("invoices.voidConfirm.title", {
            number: record.invoice_number
          }),
          content: t("invoices.voidConfirm.body"),
          okText: t("invoices.voidConfirm.ok"),
          okButtonProps: { danger: true },
          cancelText: t("invoices.voidConfirm.cancel"),
          onOk: async () => {
            try {
              await dispatch(voidInvoice(record.id)).unwrap()
              updateData()
            } catch (payload) {
              reportError(payload)
            }
          }
        })
        return
      case "send":
        setSendTarget(record)
        return
      case "change_status":
        setStatusTarget(record)
        return
      case "delivery_history":
        setDeliveryTarget(record)
        return
      case "preview":
        await preview.open(record)
        return
      case "download": {
        const blob = await dispatch(downloadInvoicePdf(record.id)).unwrap()
        downloadBlob(blob, `${record.invoice_number || "invoice"}.pdf`)
      }
    }
  }

  const handleConfirmSend = async (recipient: string) => {
    if (!sendTarget) return
    setSending(true)
    try {
      await dispatch(sendInvoice({ id: sendTarget.id, recipient })).unwrap()
      notification.success({ message: t("invoices.sent") })
      setSendTarget(null)
      updateData()
    } catch (payload) {
      reportError(payload)
    } finally {
      setSending(false)
    }
  }

  const handleConfirmStatus = async (status: number) => {
    if (!statusTarget) return
    setChangingStatus(true)
    try {
      await dispatch(
        patchGeneratedInvoice({ id: statusTarget.id, status })
      ).unwrap()
      notification.success({ message: t("invoices.changeStatus.success") })
      setStatusTarget(null)
      updateData()
    } catch (payload) {
      reportError(payload)
    } finally {
      setChangingStatus(false)
    }
  }

  const columns = useMemo(
    () =>
      getColumns({
        t: translate,
        editAllowed,
        voidAllowed,
        onSelectRow: handleSelectRow
      }),
    [translate, editAllowed, voidAllowed, filters, ordering]
  )

  const statusOptions = Object.values(INVOICE_STATUSES_MAP)
    .filter((status) => status.value !== INVOICE_STATUSES.PENDING)
    .map((status) => ({
      value: status.value,
      label: status.label
    }))

  const hasActiveFilters = Boolean(
    filters.q ||
      filters.customer ||
      filters.origin ||
      filters.status ||
      filters.date_issued_from ||
      filters.date_issued_to ||
      filters.due_date_from ||
      filters.due_date_to
  )

  const clearFilters = () => {
    dispatch(invoicesResetFilters())
  }

  const originOptions = [
    { value: INVOICE_ORIGINS.GENERATED, label: t("invoices.origin.generated") },
    { value: INVOICE_ORIGINS.EXTERNAL, label: t("invoices.origin.external") }
  ]

  return (
    <Flex vertical gap={16} className="p-6">
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Title level={3} className="m-0">
          {t("invoices.title")}
        </Title>
        <Flex gap={8} wrap>
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={handleExport}
          >
            {t("invoices.export")}
          </Button>
          {editAllowed && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openNewInvoice}
            >
              {t("invoices.newInvoice")}
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex gap={8} wrap>
        <Input.Search
          allowClear
          placeholder={t("invoices.searchPlaceholder")}
          style={{ width: 280 }}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onSearch={(value) =>
            dispatch(invoicesSetFilters({ q: value, page: 1 }))
          }
        />
        <Select
          allowClear
          placeholder={t("invoices.columns.origin")}
          style={{ width: 160 }}
          options={originOptions}
          value={filters.origin ?? undefined}
          onChange={(value) =>
            dispatch(invoicesSetFilters({ origin: value ?? null, page: 1 }))
          }
        />
        <Select
          allowClear
          placeholder={t("invoices.columns.status")}
          style={{ width: 170 }}
          options={statusOptions}
          value={filters.status ?? undefined}
          onChange={(value) =>
            dispatch(invoicesSetFilters({ status: value ?? null, page: 1 }))
          }
        />
        <Select
          allowClear
          showSearch
          filterOption={false}
          placeholder={t("invoices.filters.customer")}
          style={{ width: 200 }}
          options={customerOptions}
          notFoundContent={customerSearching ? <Spin size="small" /> : null}
          value={filters.customer ?? undefined}
          onSearch={searchCustomers}
          onChange={(value) =>
            dispatch(invoicesSetFilters({ customer: value ?? null, page: 1 }))
          }
        />
        <RangePicker
          allowClear
          placeholder={[
            t("invoices.filters.dateIssuedFrom"),
            t("invoices.filters.dateIssuedTo")
          ]}
          onChange={(range) =>
            dispatch(
              invoicesSetFilters({
                date_issued_from: range?.[0] ? normalizeDate(range[0]) : null,
                date_issued_to: range?.[1] ? normalizeDate(range[1]) : null,
                page: 1
              })
            )
          }
        />
        <RangePicker
          allowClear
          placeholder={[
            t("invoices.filters.dueDateFrom"),
            t("invoices.filters.dueDateTo")
          ]}
          onChange={(range) =>
            dispatch(
              invoicesSetFilters({
                due_date_from: range?.[0] ? normalizeDate(range[0]) : null,
                due_date_to: range?.[1] ? normalizeDate(range[1]) : null,
                page: 1
              })
            )
          }
        />
      </Flex>

      <div ref={tableRef}>
        <EntityTable<InvoiceListItem>
          surface={TABLE_SURFACES.invoices}
          selectionMode="on-demand"
          selectionFilters={filters}
          tab={DEFAULT_TAB}
          columns={columns}
          dataSource={items}
          loading={fetching && !items?.length}
          rowKey="id"
          locale={{
            emptyText: fetchFailed ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("invoices.error")}
              >
                <Button icon={<ReloadOutlined />} onClick={updateData}>
                  {t("invoices.retry")}
                </Button>
              </Empty>
            ) : hasActiveFilters ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("invoices.emptyFiltered")}
              >
                <Button onClick={clearFilters}>
                  {t("invoices.clearFilters")}
                </Button>
              </Empty>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("invoices.empty")}
              >
                {editAllowed && (
                  <Button type="primary" onClick={openNewInvoice}>
                    {t("invoices.newInvoice")}
                  </Button>
                )}
              </Empty>
            )
          }}
          onScroll={onTableScroll}
          withOutPagination
          pagination={false}
          onChange={(_pagination, _filters, sorter) => {
            const sorterValue = Array.isArray(sorter) ? sorter[0] : sorter
            const nextOrdering = sorterValue?.order
              ? `${sorterValue.order === "descend" ? "-" : ""}${String(
                  sorterValue.field
                )}`
              : null
            if (nextOrdering !== ordering) {
              dispatch(invoicesSetOrdering(nextOrdering))
            }
          }}
          scroll={{ x: 1280, y: tableHeight }}
        />
        <TableResultCount
          loaded={items?.length ?? 0}
          total={count}
          loading={fetching}
          next={next}
          retryNext={retryNext}
          onRetry={handleLoadMore}
        />
      </div>

      <IssueInvoiceDrawer
        open={drawerOpen}
        invoice={editingInvoice ?? undefined}
        onClose={() => {
          setDrawerOpen(false)
          setEditingInvoice(null)
        }}
        onSaved={() => {
          setDrawerOpen(false)
          setEditingInvoice(null)
          updateData()
        }}
      />

      <SendInvoiceConfirmModal
        open={Boolean(sendTarget)}
        defaultRecipient={sendTarget?.customer_email}
        loading={sending}
        onConfirm={handleConfirmSend}
        onCancel={() => setSendTarget(null)}
      />

      <ChangeInvoiceStatusModal
        open={Boolean(statusTarget)}
        currentStatus={statusTarget?.status}
        loading={changingStatus}
        onConfirm={handleConfirmStatus}
        onCancel={() => setStatusTarget(null)}
      />

      <InvoiceDeliveryHistoryModal
        open={Boolean(deliveryTarget)}
        invoiceId={deliveryTarget?.id}
        onClose={() => setDeliveryTarget(null)}
      />

      {preview.loading && <Spin fullscreen />}
      <FilePreviewModal
        visible={Boolean(preview.doc)}
        doc={preview.doc}
        onClose={preview.close}
      />
    </Flex>
  )
}

export default InvoicesPage
